import Vue from 'vue'
import axios from '@/assets/js/axios'
import SparkMD5 from 'spark-md5'

/**
 * 根据文件大小动态计算分片大小
 * < 10MB: 不分片，直接上传
 * 10MB ~ 100MB: 1MB/片
 * 100MB ~ 500MB: 2MB/片
 * > 500MB: 5MB/片
 */
const getChunkSize = (fileSize) => {
    if (fileSize < 1 * 1024 * 1024) return 0 // 不分片
    if (fileSize < 100 * 1024 * 1024) return 128 * 1024 // 128KB
    if (fileSize < 500 * 1024 * 1024) return 256 * 1024 // 256MB
    return 512 * 1024  // 512MB
}

/** 带重试的请求，最多重试 retryCount 次 */
const requestWithRetry = async (requestFn, retryCount = 3) => {
    for (let i = 0; i < retryCount; i++) {
        try {
            return await requestFn()
        } catch (err) {
            if (i === retryCount - 1) throw err
        }
    }
}

/**
 * @param {File} file 上传的文件
 * @param {(percentage: number) => void | undefined} onProgress 回调函数，监听进度
 * @returns {Promise<{name: string, size: string, url: string} | null>} 
 */
export const uploadFile = async (file, onProgress) => {
    try {
        const size = file.size
        // 建议增加随机数防止同一毫秒内 MD5 相同
        const hash = new SparkMD5().append(`${file.name}${new Date().getTime()}${Math.random()}`).end()
        const uploadUrl = Vue.prototype.$diskUrl + '/FragmentUpload/Upload'
        const CONCURRENCY = 5

        const CHUNK_SIZE = getChunkSize(size)

        // 小文件直接整体上传，不分片
        if (CHUNK_SIZE === 0) {
            const formData = new FormData()
            formData.append('file', file)
            formData.append('chunk', '0')
            formData.append('guid', hash)
            await requestWithRetry(() => axios({
                method: 'POST',
                url: uploadUrl,
                data: formData
            }))
            onProgress && onProgress(100)
        } else {
            // 大文件分片上传
            const chunks = []
            for (let i = 0; i * CHUNK_SIZE < size; i++) {
                chunks.push(file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE))
            }
            const formDataList = chunks.map((blob, i) => {
                const formData = new FormData()
                formData.append('file', blob)
                formData.append('chunk', i.toString())
                formData.append('guid', hash)
                return formData
            })
            
            const chunkProgress = new Array(formDataList.length).fill(0)
            const totalChunks = formDataList.length
            
            const updateProgress = () => {
                const totalProgress = chunkProgress.reduce((sum, p) => sum + p, 0)
                const overallProgress = Math.floor(totalProgress / totalChunks)
                onProgress && onProgress(Math.min(overallProgress, 99)) // 合并前最高保持 99%
            }
            
            const uploadChunk = async (formData, chunkIndex) => {
                await requestWithRetry(() => axios({
                    method: 'POST',
                    url: uploadUrl,
                    data: formData,
                    onUploadProgress: (e) => {
                        if (e.total > 0) {
                            const percent = (e.loaded / e.total) * 100
                            chunkProgress[chunkIndex] = percent
                            updateProgress()
                        }
                    }
                }))
                chunkProgress[chunkIndex] = 100
                updateProgress()
            }

            // 安全控制并发池
            let nextIndex = 0
            const pool = []
            
            const taskRunner = async () => {
                while (nextIndex < formDataList.length) {
                    const currentIndex = nextIndex++
                    await uploadChunk(formDataList[currentIndex], currentIndex)
                }
            }

            // 创建固定数量的线程并发执行
            const threadCount = Math.min(CONCURRENCY, formDataList.length)
            for (let i = 0; i < threadCount; i++) {
                pool.push(taskRunner())
            }

            // 等待所有切片严格上传完成
            await Promise.all(pool)
        }

        // 所有切片成功上传后，发起合并请求
        const res = await axios({
            method: 'GET',
            url: Vue.prototype.$diskUrl + '/FragmentUpload/Merge',
            params: {
                guid: hash,
                filename: file.name
            }
        })

        // 合并成功，进度推到 100%
        onProgress && onProgress(100)

        if (res.data && res.data.filePath) {
            return {
                remarkName: file.name,
                fileSize: file.size,
                fileUrl: 'https://crm.qixinerp.com/ossfile' + res.data.filePath,
                fileName: res.data.remarkName,
                fileMd5: res.data.fileMD5,
                m3u8Path: res.data.m3u8FilePath,
            }
        } else {
            throw new Error('Merge response error')
        }

    } catch (err) {
        // 排查关键：在控制台打出错误，避免静默失败
        console.error('文件上传/合并失败:', err)
        return null
    }
}